import React, { useCallback, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";

/**
 * Brief 14d — Panel B: COLP Review Queue.
 *
 * COLP-only. Parent page gates rendering — this component does not
 * repeat the role check.
 *
 * Brief 14e — accepts an optional ``reviews`` prop (list of
 * `{review_id, matter_ref}` records); when present, renders a
 * per-row "Generate PDF" button that POSTs to
 * ``/compliance/dashboard/evidence-snapshot/{matter_ref}`` and forces
 * a browser download.
 */
export default function COLPReviewPanel({ data, loading, error, reviews }) {
  if (loading) {
    return (
      <section
        data-testid="colp-review-panel"
        className="rounded-xl border border-white/[0.07] bg-navy-card p-4 space-y-3"
      >
        <div data-testid="colp-review-panel-skeleton" className="h-4 w-44 bg-slate-800 rounded animate-pulse" />
        <div className="h-12 w-full bg-slate-800/70 rounded animate-pulse" />
      </section>
    );
  }

  if (error) {
    return (
      <section
        data-testid="colp-review-panel"
        className="rounded-md border border-red-800/50 bg-red-950/30 p-4 text-sm text-red-200"
      >
        <span data-testid="colp-review-panel-error">Dashboard data unavailable. Try reloading.</span>
      </section>
    );
  }

  const d = data || {};
  const pending = d.pending_count ?? 0;
  const decidedMonth = d.decided_this_month ?? 0;
  const od = d.outcome_distribution || {};
  const openRemand = d.open_remand_cycles ?? 0;

  const outcomes = [
    { key: "approve", label: "Approve" },
    { key: "approve_with_conditions", label: "Approve with conditions" },
    { key: "reject", label: "Reject" },
    { key: "remand_to_mlco", label: "Remand to MLCO" },
  ];

  return (
    <section
      data-testid="colp-review-panel"
      className="rounded-xl border border-white/[0.07] bg-navy-card p-4 space-y-4"
    >
      <h2 className="text-sm font-semibold text-slate-100">COLP Review Queue</h2>

      <div data-testid="colp-pending" className="text-sm text-slate-200">
        <span className="text-lg font-semibold text-slate-100">{pending}</span>{" "}
        matter{pending === 1 ? "" : "s"} awaiting COLP sign-off.
      </div>

      <div data-testid="colp-outcome-distribution">
        <h3 className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Outcomes (all time)</h3>
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
          {outcomes.map((oc, i) => (
            <div key={oc.key} data-testid={`outcome-row-${oc.key}`}
              className="relative overflow-hidden rounded-lg border border-white/[0.07] bg-slate-900/40 px-3 py-2">
              <div aria-hidden className="absolute inset-x-0 top-0 h-0.5" style={{ background: OUTCOME_HUES[i % OUTCOME_HUES.length] }} />
              <div className="text-[11px] text-slate-400 truncate">{oc.label}</div>
              <div className="text-xl font-bold text-white tabular-nums">{od[oc.key] ?? 0}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-sm text-slate-200" data-testid="colp-decided-this-month">
        <span className="text-slate-100 font-semibold">{decidedMonth}</span> matter
        {decidedMonth === 1 ? "" : "s"} decided this month.
      </div>

      {openRemand > 0 && (
        <div
          className="rounded-md border border-amber-700/60 bg-amber-900/30 text-amber-200 text-sm px-3 py-2"
          data-testid="colp-remand-callout"
        >
          {openRemand} matter{openRemand === 1 ? "" : "s"} in an open remand cycle (COLP
          remanded; MLCO has not yet re-decided).{" "}
          <a className="underline" href="/compliance/mlco-inbox" data-testid="colp-remand-link">
            View
          </a>
        </div>
      )}

      <a
        href="/compliance/mlco-inbox"
        className="text-xs text-indigo-400 hover:underline"
        data-testid="colp-review-inbox-link"
      >
        View MLCO Inbox →
      </a>

      {Array.isArray(reviews) && reviews.length > 0 && (
        <EvidenceSnapshotList reviews={reviews} />
      )}
    </section>
  );
}

// Top stripe per outcome tile; never green, amber or red (status colours).
const OUTCOME_HUES = [
  "linear-gradient(90deg, #6366f1, #a855f7)",
  "linear-gradient(90deg, #8b5cf6, #d946ef)",
  "linear-gradient(90deg, #3b82f6, #6366f1)",
  "linear-gradient(90deg, #06b6d4, #3b82f6)",
];

// ── Brief 14e — per-review evidence snapshot generator ───────────────

function EvidenceSnapshotList({ reviews }) {
  return (
    <div
      data-testid="evidence-snapshot-list"
      className="rounded-md border border-slate-700/50 bg-slate-800/40 p-3 space-y-2"
    >
      <h3 className="text-xs uppercase tracking-wider text-slate-400">
        Evidence snapshots
      </h3>
      <ul className="space-y-1">
        {reviews.map((r) => (
          <EvidenceSnapshotRow
            key={r.review_id}
            reviewId={r.review_id}
            matterRef={r.matter_ref}
          />
        ))}
      </ul>
    </div>
  );
}

function EvidenceSnapshotRow({ reviewId, matterRef }) {
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [errMsg, setErrMsg] = useState("");

  const onClick = useCallback(async () => {
    setState("loading");
    setErrMsg("");
    try {
      const email =
        sessionStorage.getItem("bonsai_user_id") ||
        localStorage.getItem("bonsai_user_id") ||
        "";
      const res = await axios.post(
        `${API}/compliance/dashboard/evidence-snapshot/${encodeURIComponent(matterRef)}`,
        { review_id: reviewId },
        {
          responseType: "blob",
          headers: {
            "X-Bonsai-User-Id": email,
            "Content-Type": "application/json",
          },
        }
      );
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m
        ? m[1]
        : `Evidence-Snapshot-${matterRef}-${new Date().toISOString().slice(0, 10)}.pdf`;
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setState("done");
    } catch (err) {
      const status = err?.response?.status;
      setErrMsg(status ? `request failed (${status})` : "request failed");
      setState("error");
    }
  }, [reviewId, matterRef]);

  return (
    <li className="flex items-center gap-2 text-sm" data-testid="evidence-snapshot-row">
      <span className="text-slate-300 flex-1">
        <span className="font-mono">{matterRef}</span>{" "}
        <span className="text-slate-500 text-xs">({reviewId.slice(0, 6)}…)</span>
      </span>
      <button
        type="button"
        onClick={onClick}
        disabled={state === "loading"}
        className="px-2 py-1 rounded-md text-xs font-medium bg-indigo-700/80 text-white hover:bg-indigo-600 disabled:opacity-50"
        data-testid={`evidence-snapshot-btn-${reviewId}`}
      >
        {state === "loading" ? "Generating…" : state === "done" ? "Re-generate PDF" : "Generate PDF"}
      </button>
      {state === "error" && (
        <span
          className="text-xs text-red-400"
          data-testid={`evidence-snapshot-err-${reviewId}`}
        >
          {errMsg}
        </span>
      )}
    </li>
  );
}
